import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useQuery } from "react-query";
import {
  FiUsers,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiMapPin,
  FiMail,
  FiPhone,
  FiDollarSign,
  FiPackage,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { vendorsAPI } from "../../services/api";
import LoadingSpinner from "../../components/LoadingSpinner";
import toast from "react-hot-toast";

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #2d3436;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Subtitle = styled.p`
  color: #636e72;
  font-size: 1.1rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${(props) => props.$color || "#2d5016"};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => props.$bgColor || "#f8f9fa"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color || "#2d5016"};
  font-size: 1.2rem;
`;

const StatLabel = styled.span`
  color: #636e72;
  font-size: 0.9rem;
  font-weight: 500;
`;

const StatValue = styled.div`
  color: #2d3436;
  font-size: 2rem;
  font-weight: 700;
  margin-top: 0.5rem;
`;

const FiltersContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const SubscribersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const SubscriberCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const SubscriberHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const SubscriberInfo = styled.div`
  flex: 1;
`;

const SubscriberName = styled.h3`
  color: #2d3436;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const SubscriberEmail = styled.p`
  color: #636e72;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SubscriberPhone = styled.p`
  color: #636e72;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.active {
    background: #d4edda;
    color: #155724;
  }

  &.cancelled {
    background: #f8d7da;
    color: #721c24;
  }

  &.completed {
    background: #e2e3e5;
    color: #383d41;
  }
`;

const MealPlanInfo = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const MealPlanTitle = styled.h4`
  color: #2d3436;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MealPlanDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #636e72;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddressInfo = styled.div`
  background: #f0f8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const AddressTitle = styled.h5`
  color: #2d5016;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddressText = styled.p`
  color: #2d5016;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const SubscriptionDates = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DateItem = styled.div`
  text-align: center;
`;

const DateLabel = styled.span`
  color: #636e72;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DateValue = styled.div`
  color: #2d3436;
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: 0.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #636e72;

  svg {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #2d3436;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const VendorSubscribers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch subscribers data
  const {
    data: subscribersData,
    isLoading,
    error,
    refetch,
  } = useQuery("vendor-subscribers", () => vendorsAPI.getSubscribers(), {
    keepPreviousData: true,
  });

  const subscribers = subscribersData?.data?.data?.subscribers || [];

  // Filter subscribers based on search and status
  const filteredSubscribers = subscribers.filter((subscriber) => {
    const matchesSearch =
      subscriber.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.meal_plan_title
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || subscriber.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Calculate statistics
  const stats = {
    total: subscribers.length,
    active: subscribers.filter((s) => s.status === "active").length,
    cancelled: subscribers.filter((s) => s.status === "cancelled").length,
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GY", {
      style: "currency",
      currency: "GYD",
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <FiCheckCircle />;
      case "cancelled":
        return <FiXCircle />;
      case "completed":
        return <FiAlertCircle />;
      default:
        return <FiClock />;
    }
  };

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>
            <FiUsers />
            Subscribers
          </Title>
          <Subtitle>View customers who have purchased your meal plans</Subtitle>
        </Header>
        <LoadingSpinner />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>
            <FiUsers />
            Subscribers
          </Title>
          <Subtitle>View customers who have purchased your meal plans</Subtitle>
        </Header>
        <EmptyState>
          <FiAlertCircle />
          <h3>Error Loading Subscribers</h3>
          <p>Failed to load subscriber data. Please try again.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FiUsers />
          Subscribers
        </Title>
        <Subtitle>Manage your meal plan subscribers</Subtitle>
      </Header>

      <StatsContainer>
        <StatCard
          $color="#2d5016"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#f0f8f0" $color="#2d5016">
              <FiUsers />
            </StatIcon>
            <StatLabel>Total Subscribers</StatLabel>
          </StatHeader>
          <StatValue>{stats.total}</StatValue>
        </StatCard>

        <StatCard
          $color="#28a745"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#d4edda" $color="#28a745">
              <FiCheckCircle />
            </StatIcon>
            <StatLabel>Active</StatLabel>
          </StatHeader>
          <StatValue>{stats.active}</StatValue>
        </StatCard>

        <StatCard
          $color="#dc3545"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#f8d7da" $color="#dc3545">
              <FiXCircle />
            </StatIcon>
            <StatLabel>Cancelled</StatLabel>
          </StatHeader>
          <StatValue>{stats.cancelled}</StatValue>
        </StatCard>
      </StatsContainer>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search subscribers by name, email, or meal plan..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
        </FilterSelect>
      </FiltersContainer>

      {filteredSubscribers.length === 0 ? (
        <EmptyState>
          <FiUsers />
          <h3>No Subscribers Found</h3>
          <p>
            {searchTerm || statusFilter !== "all"
              ? "No subscribers match your current filters."
              : "You don't have any subscribers yet. Create meal plans to start attracting customers!"}
          </p>
        </EmptyState>
      ) : (
        <SubscribersGrid>
          {filteredSubscribers.map((subscriber, index) => (
            <SubscriberCard
              key={subscriber.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <SubscriberHeader>
                <SubscriberInfo>
                  <SubscriberName>
                    {subscriber.first_name} {subscriber.last_name}
                  </SubscriberName>
                  <SubscriberEmail>
                    <FiMail />
                    {subscriber.email}
                  </SubscriberEmail>
                  {subscriber.phone && (
                    <SubscriberPhone>
                      <FiPhone />
                      {subscriber.phone}
                    </SubscriberPhone>
                  )}
                </SubscriberInfo>
                <StatusBadge className={subscriber.status}>
                  {getStatusIcon(subscriber.status)}
                  {subscriber.status}
                </StatusBadge>
              </SubscriberHeader>

              <MealPlanInfo>
                <MealPlanTitle>
                  <FiPackage />
                  {subscriber.meal_plan_title}
                </MealPlanTitle>
                <MealPlanDetails>
                  <DetailItem>
                    <FiDollarSign />
                    {(() => {
                      // Calculate the correct price based on fulfillment method
                      const basePrice = parseFloat(subscriber.meal_plan_base_price || subscriber.meal_plan_price || 0);
                      const deliveryFee = parseFloat(subscriber.meal_plan_delivery_fee || 0);
                      
                      let displayPrice;
                      if (subscriber.total_amount && subscriber.total_amount > 0) {
                        // Use the stored total_amount if available and valid
                        displayPrice = parseFloat(subscriber.total_amount);
                      } else if (subscriber.fulfillment_method === "delivery") {
                        // For delivery: base price + delivery fee
                        displayPrice = basePrice + deliveryFee;
                      } else {
                        // For pickup: just base price
                        displayPrice = basePrice;
                      }
                      
                      return formatCurrency(displayPrice);
                    })()}
                    {subscriber.fulfillment_method === "delivery" && (
                      <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "0.5rem" }}>
                        (Delivery)
                      </span>
                    )}
                    {subscriber.fulfillment_method === "pickup" && (
                      <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "0.5rem" }}>
                        (Pickup)
                      </span>
                    )}
                  </DetailItem>
                  <DetailItem>
                    <FiCalendar />
                    {subscriber.plan_type}
                  </DetailItem>
                </MealPlanDetails>
              </MealPlanInfo>

              {(subscriber.address_line1 ||
                subscriber.fulfillment_method === "pickup") && (
                <AddressInfo>
                  <AddressTitle>
                    <FiMapPin />
                    {subscriber.fulfillment_method === "pickup"
                      ? "Pickup"
                      : "Delivery Address"}
                  </AddressTitle>
                  <AddressText>
                    {subscriber.fulfillment_method === "pickup"
                      ? "Customer will pickup at vendor location"
                      : `${subscriber.address_line1}${subscriber.address_line2 ? `, ${subscriber.address_line2}` : ""}, ${subscriber.city}, ${subscriber.region}`}
                  </AddressText>
                </AddressInfo>
              )}

              <SubscriptionDates>
                <DateItem>
                  <DateLabel>Start Date</DateLabel>
                  <DateValue>{formatDate(subscriber.start_date)}</DateValue>
                </DateItem>
                <DateItem>
                  <DateLabel>End Date</DateLabel>
                  <DateValue>{formatDate(subscriber.end_date)}</DateValue>
                </DateItem>
              </SubscriptionDates>

              {subscriber.special_instructions && (
                <div style={{ marginTop: "1rem" }}>
                  <h5
                    style={{
                      color: "#2d3436",
                      fontSize: "0.9rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Special Instructions:
                  </h5>
                  <p
                    style={{
                      color: "#636e72",
                      fontSize: "0.9rem",
                      lineHeight: "1.4",
                    }}
                  >
                    {subscriber.special_instructions}
                  </p>
                </div>
              )}
            </SubscriberCard>
          ))}
        </SubscribersGrid>
      )}
    </Container>
  );
};

export default VendorSubscribers;
